<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useClipboard } from '@vueuse/core';
import { useUserStore } from '@/stores/user';
import { generateMagicLink } from '@/api/devtools';
import useToaster from '@/composables/useToaster';

/** Sentry org/project slugs — used to build issue and log explorer links. */
const SENTRY_ORG = 'jenkaa-s0';
const SENTRY_PROJECT_ID = '4510221035765840';

const userStore = useUserStore();
const route = useRoute();
const toaster = useToaster();
const { copy } = useClipboard({ legacy: true });

const isOpen = ref(false);
const isLoading = ref(false);
const report = ref('');

const hasUser = computed(() => userStore.user !== null);

type MagicLink = { url: string; expiry: string } | { url: null; expiry: null };

const fetchMagicLink = async (): Promise<MagicLink> => {
  try {
    const { magicLink, expirationDate } = await generateMagicLink(route.fullPath);
    return { url: magicLink, expiry: new Date(expirationDate).toLocaleString('fr-FR') };
  } catch {
    return { url: null, expiry: null };
  }
};

const SEPARATOR = '────────────────────────────────────────';

/** Sentry time window: 5 min before to 10 sec after the report moment. */
const WINDOW_BEFORE_MS = 5 * 60 * 1000;
const WINDOW_AFTER_MS = 10 * 1000;

const buildReport = async () => {
  const user = userStore.user;
  if (!user) return;

  isLoading.value = true;
  try {
    const env = import.meta.env.MODE;
    const email = user.email;
    const now = new Date();
    const start = new Date(now.getTime() - WINDOW_BEFORE_MS).toISOString();
    const end = new Date(now.getTime() + WINDOW_AFTER_MS).toISOString();
    const userQuery = encodeURIComponent(`user.email:${email}`);
    const timeRange = `start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&utc=true`;
    const issuesUrl = `https://${SENTRY_ORG}.sentry.io/issues/?query=${userQuery}&environment=${env}&project=${SENTRY_PROJECT_ID}&${timeRange}`;
    const logsUrl = `https://${SENTRY_ORG}.sentry.io/explore/logs/?query=${userQuery}&environment=${env}&${timeRange}`;
    const magicLink = await fetchMagicLink();
    const magicLinkValue = magicLink.url
      ? `${magicLink.url}\n              (expire ${magicLink.expiry})`
      : '<génération échouée>';

    report.value = [
      SEPARATOR,
      `  BUG REPORT — ${now.toISOString()}`,
      SEPARATOR,
      '',
      'USER',
      `  Name      : ${user.firstName} ${user.lastName}`,
      `  Email     : ${email}`,
      `  ID        : ${user.id}`,
      `  Role      : ${user.role}`,
      '',
      'CONTEXT',
      `  Env       : ${env}`,
      `  App URL   : ${window.location.href}`,
      `  Route     : ${String(route.name ?? '-')}  (params: ${JSON.stringify(route.params)})`,
      `  Version   : ${__APP_VERSION__}`,
      '',
      'MAGIC LINK',
      `  ${magicLinkValue}`,
      '',
      'SENTRY',
      `  Issues    : ${issuesUrl}`,
      `  Logs      : ${logsUrl}`,
      '',
      'CLIENT',
      `  Browser   : ${navigator.userAgent}`,
      `  Viewport  : ${window.innerWidth}×${window.innerHeight}`,
      `  Screen    : ${window.screen.width}×${window.screen.height} (DPR ${window.devicePixelRatio})`,
      `  Locale    : ${navigator.language}`,
      `  Timezone  : ${Intl.DateTimeFormat().resolvedOptions().timeZone}`,
      SEPARATOR,
    ].join('\n');
  } finally {
    isLoading.value = false;
  }
};

const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    // eslint-disable-next-line no-console
    buildReport().catch((err) => console.error('[TestTools] buildReport failed', err));
  }
};

const copyReport = async () => {
  await buildReport();
  copy(report.value);
  toaster.success('Rapport copié !');
};
</script>

<template>
  <div class="testtools">
    <button class="testtools-toggle" @click="toggle">TEST</button>

    <div v-if="isOpen" class="testtools-panel">
      <div class="testtools-header">
        <h3>TEST - Bug Report</h3>
        <button class="testtools-close" @click="isOpen = false">X</button>
      </div>

      <div class="testtools-content">
        <p v-if="!hasUser" class="testtools-empty">Aucun utilisateur connecté</p>
        <q-spinner v-else-if="isLoading" color="warning" size="2em" />
        <template v-else>
          <pre class="testtools-report">{{ report }}</pre>
          <q-btn
            color="warning"
            label="Copier le rapport"
            class="full-width q-mt-md"
            @click="copyReport"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.testtools {
  position: fixed;
  bottom: 16px;
  /* Offset left of the DEV button so both can show side by side in development. */
  right: 80px;
  z-index: 9999;
  font-family: monospace;
}

.testtools-toggle {
  background: #1a1a2e;
  color: #ffb020;
  border: 2px solid #ffb020;
  padding: 8px 12px;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  font-size: 12px;

  &:hover {
    background: #ffb020;
    color: #1a1a2e;
  }
}

.testtools-panel {
  position: absolute;
  bottom: 48px;
  right: 0;
  width: 500px;
  max-height: 600px;
  background: #1a1a2e;
  border: 1px solid #333;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.testtools-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #0f0f1a;
  border-bottom: 1px solid #333;

  h3 {
    margin: 0;
    color: #ffb020;
    font-size: 14px;
  }
}

.testtools-close {
  background: transparent;
  border: none;
  color: #ff6b6b;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;

  &:hover {
    color: #ff8888;
  }
}

.testtools-content {
  flex: 1;
  overflow: auto;
  color: #e0e0e0;
  padding: 16px;
}

.testtools-empty {
  color: #888;
}

.testtools-report {
  background: #0f0f1a;
  padding: 12px;
  border-radius: 4px;
  overflow: auto;
  font-size: 11px;
  color: #ffb020;
  max-height: 420px;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
